import { chains } from '@emberswap/emberswap-sdk2/dist/constants'
import { Amount, Native } from '@emberswap/emberswap-sdk2/dist/entities'
import { ChainId } from '@emberswap/emberswap-sdk2/dist/enums'
import { isChainSupported } from '@emberswap/emberswap-sdk2/dist/functions'
import { Address } from '@emberswap/emberswap-sdk2/dist/types'
import { JSBI } from '@emberswap/emberswap-sdk2/dist/utils'
import { Listbox } from '@headlessui/react'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import { Chain, useBalance, useConnect, useWalletClient } from 'wagmi'

import { classNames } from '../../../functions'
import { useSwitchNetwork as switchNetworkTo } from '../../../hooks/useSwitchNetwork'
import { useDebounce } from '../../../hooks/utils'
import { useSessionChainId } from '../../../state/application/hooks'
import useActiveWeb3React from '../../../wagmi/hooks/useActiveWeb3React'
import { AnimationTransition } from '../../Animation'
import { NetworkIcon, WarningIconRound } from '../../Icons'
/**
 * @param {chainId[]} supportedNetworks
 * @return {JSX.Element} Network Selector
 */
export default function NetworkSwitch({
  supportedNetworks,
  children,
}: {
  supportedNetworks?: ChainId[]
  children: ReactNode
}) {
  const { isConnected, chain, account } = useActiveWeb3React()
  const { connect, connectors } = useConnect()
  const walletClient = useWalletClient({chainId: chain?.id})
  const [localChainId, { updateChainId }] = useSessionChainId()
  const networks = useMemo(() => Array.from(new Set(supportedNetworks)), [supportedNetworks])
  const [hover, setHover] = useState(false)
  const [debouncedHover, setDebouncedHover] = useState(false)

  const DebouncedHover = useDebounce([debouncedHover, hover], 200)
  useEffect(() => {
    const [debouncedHover] = DebouncedHover
    setHover(debouncedHover)
  }, [DebouncedHover])

  return (
    <Listbox
      as="div"
      value={chain ? '' : localChainId}
      onChange={chain ? () => '' : (chainId: ChainId) => updateChainId(chainId)}
      className="flex justify-center w-full"
    >
      <span className="group px-2 py-1 flex items-center -ml-0.5">
        <Listbox.Button
          onMouseEnter={() => {
            setHover(true)
            setDebouncedHover(true)
          }}
          onMouseLeave={() => setDebouncedHover(false)}
          className="flex flex-col gap-2 text-xs font-bold duration-500 ease-in-out border border-transparent group-hover:border-green-300 group-hover:border-opacity-70 rounded-xl opacity-80 group-hover:opacity-100 align-center focus:outline-none focus:ring-offset-transparent focus:ring-transparent"
        >
          {isChainSupported(chain?.id || localChainId) ? (
            <NetworkIcon chainId={chain ? chain.id : localChainId} width={22} height={22} />
          ) : (
            <>
              <WarningIconRound color="red" width={22} height={22} />
              {/* <Typography
              variant="sm"
              weight={400}
              className="self-center font-bold text-red-500 uppercase text-xxs "
            >
              {t('Unsupported Network')}
        </Typography>*/}
            </>
          )}
        </Listbox.Button>
        <AnimationTransition show={hover}>
          <Listbox.Options
            onMouseEnter={() => {
              setHover(true)
              setDebouncedHover(true)
            }}
            onClick={() => {
              setHover(false)
              setDebouncedHover(false)
            }}
            onMouseLeave={() => setDebouncedHover(false)}
            as="div"
            className="text-primary text-xxs font-normal group -left-1/2 -right-1/2 lg:mt-2 mx-auto landscape:mt-2 mt-auto absolute lg:bottom-auto landscape:bottom-auto top-auto landscape:top-full lg:top-full bottom-full mb-3 origin-top min-w-[275px] grid grid-cols-1 w-max overflow-auto scroll overflow-x-hidden max-h-60 px-2 py-2 gap-x-4  rounded-xl shadow-lg border border-orange-500 z-50 focus:outline-none"
          >
            {networks.map((el: ChainId) => (
              <Listbox.Option
                key={el}
                value={el}
                onClick={() => {
                  // connect({ connector })
                  isConnected && switchNetworkTo(el, walletClient, connectors, connect)
                }}
                as="div"
                className={classNames(
                  localChainId === el ? 'bg-secondarybg  text-high-emphesis' : 'text-primary',
                  'hover:opacity-80 px-2 flex rounded-xl z-50  gap-2 items-center cursor-pointer transform-all h-[40px] hover:bg-gray-500'
                )}
              >
                <NetworkIcon chainId={el} width={22} height={22} />
                <p className="mx-2 text-xs font-normal tracking-wide ">{chains[el].name}</p>
                {localChainId === el && (
                  <span
                    className={classNames(
                      isConnected && chain && chain.id && isChainSupported(chain.id)
                        ? 'bg-green-500'
                        : 'bg-red-700',
                      'ml-auto mr-4 w-2 h-2 rounded-full self-center'
                    )}
                  />
                )}
              </Listbox.Option>
            ))}
            <div className="rounded-r-lg custom-backdrop-blur z-1" />
          </Listbox.Options>
        </AnimationTransition>
      </span>
      <span className="flex items-center gap-1 pr-2 font-normal text-primary text-xxs ">
        <Balance isConnected={isConnected} chain={chain} account={account as Address} />
      </span>
      {children}
    </Listbox>
  )
}

/**
 * @return {React.ElementType<any>} Balance if connected
 */
export function Balance({
  isConnected,
  account,
  chain,
}: {
  isConnected: boolean
  account: `0x${string}`
  chain:
    | (Chain & {
        unsupported?: boolean | undefined
      })
    | undefined
}) {
  const { data /* , isError, isLoading*/ } = useBalance({
    address: account,
    enabled: !!account,
  })

  return (
    <>
      {isConnected && (
        <>
          {chain && isChainSupported(chain.id) && data && (
            <>
              {Amount.fromRawAmount(
                Native.onChain(chain.id),
                JSBI.BigInt(data.value.toString())
              ).toSignificant(4)}
              <span className="font-bold">{Native.onChain(chain.id)?.symbol}</span>
            </>
          )}
        </>
      )}
    </>
  )
}
